import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { axiosInstance } from '../config/axios';
import { toast } from 'sonner';
import TicketSelector from '../components/TicketSelector';

const patternStripes = {
  backgroundImage:
    'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,0.12) 10px, rgba(255,255,255,0.12) 20px)',
};

const iconByType = {
  Play: 'ph-soccer-ball',
  Move: 'ph-person-simple-run',
  Mind: 'ph-brain',
};

export default function EventDetailPageV2() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [booking, setBooking] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [registrationForm, setRegistrationForm] = useState({});
  const [isLiked, setIsLiked] = useState(true);

  useEffect(() => {
    axiosInstance
      .get(`/events/${id}`)
      .then((res) => {
        // Redirect tournament events to tournament detail page
        if (res.data.is_tournament) {
          navigate(`/tournaments/${id}`, { replace: true });
          return;
        }
        setEvent(res.data);
        const init = {};
        res.data.ticket_tiers.forEach((t) => (init[t.id] = 0));
        setBooking(init);
      })
      .catch(() => {
        toast.error('Failed to load event');
        navigate('/events');
      });
  }, [id, navigate]);

  const updateQty = (tid, delta) => {
    const maxTickets = event?.max_tickets_per_user ?? 1;
    setBooking((prev) => {
      const currentTotal = Object.values(prev).reduce((sum, qty) => sum + qty, 0);
      if (delta > 0 && currentTotal >= maxTickets) {
        return prev;
      }
      return {
        ...prev,
        [tid]: Math.max(0, (prev[tid] || 0) + delta),
      };
    });
  };

  const getTotal = () => {
    if (!event) return 0;
    return event.ticket_tiers.reduce((sum, tier) => sum + tier.price * (booking[tier.id] || 0), 0);
  };

  const getTotalTix = () => Object.values(booking).reduce((sum, qty) => sum + qty, 0);

  const maxTickets = event?.max_tickets_per_user ?? 1;
  const totalAmount = getTotal();
  const totalTickets = getTotalTix();
  const checkoutVisible = totalTickets > 0;
  const checkoutPadding = checkoutVisible ? '16rem' : '8rem';

  if (!event) {
    return (
      <div className="min-h-screen bg-brand-sand flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E54562]"></div>
      </div>
    );
  }

  const handleCheckout = () => {
    if (getTotalTix() === 0) {
      toast.error('Please select at least one ticket');
      return;
    }

    const registrationConfig = event?.registration_config;
    const registrationFields = registrationConfig?.fields || [];
    const registrationEnabled = Boolean(registrationConfig?.enabled);

    if (registrationEnabled) {
      const missingFields = registrationFields.filter(
        (field) => field.required && !registrationForm[field.id]
      );
      if (missingFields.length > 0) {
        toast.error(`Please complete: ${missingFields[0].label}`);
        return;
      }
    }

    const items = event.ticket_tiers
      .filter((t) => booking[t.id] > 0)
      .map((t) => ({
        tier_id: t.id,
        name: t.name,
        price: t.price,
        quantity: booking[t.id],
      }));

    const checkoutPayload = {
      eventId: event.id,
      title: event.title,
      start_time: event.start_time,
      venue: event.venue,
      items,
      totalAmount,
      registration: registrationEnabled
        ? {
            eventId: event.id,
            answers: registrationFields.map((field) => ({
              field_id: field.id,
              value: registrationForm[field.id] || '',
            })),
          }
        : null,
    };

    localStorage.setItem('eventCheckout', JSON.stringify(checkoutPayload));
    setSubmitting(true);
    navigate(`/events/${event.id}/checkout`);
  };

  const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });

  const formatTime = (dateStr) =>
    new Date(dateStr).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });

  const formatLongDate = (dateStr) =>
    new Date(dateStr).toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);

  const registrationConfig = event?.registration_config;
  const isRegistrationEvent = Boolean(registrationConfig?.enabled);
  const registrationEndDate = registrationConfig?.end_date;
  const iconClass = iconByType[event.type] || 'ph-microphone-stage';

  const handleRegistrationFieldChange = (fieldId, value) => {
    setRegistrationForm((prev) => ({
      ...prev,
      [fieldId]: value,
    }));
  };

  return (
    <div
      className="bg-gray-50 min-h-screen text-[#2B3A67]"
      style={{
        fontFamily: "'Outfit', sans-serif",
        paddingBottom: checkoutPadding,
        transition: 'padding-bottom 0.9s ease',
      }}
    >
      <header className="fixed top-0 z-50 w-full pt-safe" data-testid="event-header">
        <div className="flex items-center justify-between px-6 py-4">
          <button
            onClick={() => navigate('/events')}
            className="w-10 h-10 bg-white/90 backdrop-blur-md rounded-full flex items-center justify-center shadow-lg border border-gray-100"
            data-testid="event-back-button"
          >
            <i className="ph-bold ph-arrow-left text-xl text-[#2B3A67]"></i>
          </button>
          <div className="flex gap-2">
            <button
              className="w-10 h-10 bg-white/90 backdrop-blur-md rounded-full flex items-center justify-center shadow-lg border border-gray-100"
              data-testid="event-share-button"
            >
              <i className="ph-bold ph-share-network text-xl text-[#2B3A67]"></i>
            </button>
            <button
              onClick={() => setIsLiked(!isLiked)}
              className="w-10 h-10 bg-white/90 backdrop-blur-md rounded-full flex items-center justify-center shadow-lg border border-gray-100"
              data-testid="event-like-button"
            >
              <i className={`${isLiked ? 'ph-fill' : 'ph-bold'} ph-heart text-xl text-[#E54562]`}></i>
            </button>
          </div>
        </div>
      </header>

      <main className="w-full">
        <div className="h-[340px] w-full relative overflow-hidden" style={{ backgroundColor: '#3AAFA9' }}>
          <div className="absolute inset-0 opacity-20" style={patternStripes}></div>
          {event.image_url && (
            <img src={event.image_url} alt={event.title} className="absolute inset-0 w-full h-full object-cover opacity-20" />
          )}
          <div className="absolute -bottom-20 -right-20 w-64 h-64 rounded-full border-8 border-white/20" style={{ backgroundColor: '#F4D341' }}></div>
          <div className="absolute inset-0 flex items-center justify-center pt-8">
            <i className={`ph-duotone ${iconClass} text-[180px] text-white opacity-40 rotate-12`}></i>
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-gray-50 via-transparent to-transparent"></div>
        </div>

        <div className="px-6 -mt-12 relative z-10">
          <div className="bg-white rounded-[2rem] p-6 shadow-xl border border-gray-100">
            <div className="flex gap-2 mb-4">
              <span className="text-[10px] font-black px-2 py-1 rounded-md uppercase tracking-widest bg-[#3AAFA9]/10 text-[#3AAFA9]">
                {event.type}
              </span>
              <span className="text-[10px] font-black px-2 py-1 rounded-md uppercase tracking-widest bg-[#E54562]/10 text-[#E54562]">
                {event.status}
              </span>
            </div>
            <h1 className="text-3xl font-extrabold leading-tight mb-4" data-testid="event-title">
              {event.title}
            </h1>

            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gray-50 rounded-2xl flex items-center justify-center border border-gray-100 text-[#E54562]">
                  <i className="ph-fill ph-calendar-blank text-2xl"></i>
                </div>
                <div>
                  <p className="text-sm font-bold" data-testid="event-date">
                    {formatDate(event.start_time)}
                  </p>
                  <p className="text-xs text-gray-500 font-medium" data-testid="event-time">
                    {formatTime(event.start_time)} - {formatTime(event.end_time || event.start_time)}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gray-50 rounded-2xl flex items-center justify-center border border-gray-100 text-[#3AAFA9]">
                  <i className="ph-fill ph-map-pin text-2xl"></i>
                </div>
                <div>
                  <p className="text-sm font-bold" data-testid="event-venue">{event.venue.name}</p>
                  <p className="text-xs text-gray-500 font-medium" data-testid="event-address">
                    {event.venue.address}, {event.venue.city}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 flex items-center justify-between bg-white p-4 rounded-2xl border border-gray-100 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full p-0.5 border-2 border-[#F4D341]">
                <div className="w-full h-full bg-gray-200 rounded-full flex items-center justify-center text-xs font-bold text-gray-500">
                  {event.organizer?.name?.slice(0, 2)?.toUpperCase() || 'BY'}
                </div>
              </div>
              <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Hosted By</p>
                <p className="text-sm font-bold">{event.organizer?.name || 'Backyard Live'}</p>
              </div>
            </div>
            <button className="text-white text-xs font-bold px-4 py-2 rounded-lg bg-[#2B3A67]" data-testid="event-follow-host">
              Follow
            </button>
          </div>

          <div className="mt-8">
            <h3 className="font-bold text-lg mb-3">About Event</h3>
            <p className="text-gray-500 text-sm leading-relaxed" data-testid="event-description">
              {event.description}
            </p>
            {isRegistrationEvent && registrationEndDate && (
              <p className="text-xs text-gray-500 mt-3" data-testid="event-registration-end">
                Registration closes on {formatLongDate(registrationEndDate)}
              </p>
            )}
          </div>

          {/* Tournament Registration CTA for Cricket Events */}
          {event.is_tournament && (
            <div className="mt-8 bg-gradient-to-br from-[#2B3A67] to-[#1e2a4a] rounded-2xl p-6 text-white" data-testid="tournament-registration-cta">
              <div className="flex items-center gap-2 mb-3">
                <i className="ph-bold ph-trophy text-[#F4D341] text-xl" />
                <h3 className="font-extrabold text-lg">Tournament Registration</h3>
              </div>
              {event.cricket_config && (
                <div className="flex gap-2 flex-wrap mb-4">
                  {event.cricket_config.format_label && <span className="text-xs bg-white/15 px-2 py-1 rounded-full">{event.cricket_config.format_label}</span>}
                  {event.cricket_config.ball_type_label && <span className="text-xs bg-white/15 px-2 py-1 rounded-full">{event.cricket_config.ball_type_label}</span>}
                  {event.cricket_config.category_label && <span className="text-xs bg-white/15 px-2 py-1 rounded-full">{event.cricket_config.category_label}</span>}
                </div>
              )}
              <p className="text-sm text-white/70 mb-4">Register as a Player, Team Owner, or Sponsor for this tournament.</p>
              <div className="flex gap-2 text-xs mb-4">
                {event.cricket_config?.player_fee > 0 && <span className="bg-white/10 px-2.5 py-1 rounded-lg">Player: ₹{(event.cricket_config.player_fee / 100).toLocaleString()}</span>}
                {event.cricket_config?.team_owner_fee > 0 && <span className="bg-white/10 px-2.5 py-1 rounded-lg">Team Owner: ₹{(event.cricket_config.team_owner_fee / 100).toLocaleString()}</span>}
                {event.cricket_config?.sponsor_fee > 0 && <span className="bg-white/10 px-2.5 py-1 rounded-lg">Sponsor: ₹{(event.cricket_config.sponsor_fee / 100).toLocaleString()}</span>}
                {event.cricket_config?.player_fee === 0 && event.cricket_config?.team_owner_fee === 0 && event.cricket_config?.sponsor_fee === 0 && <span className="bg-white/10 px-2.5 py-1 rounded-lg">Free Registration</span>}
              </div>
              <button
                onClick={() => navigate(`/events/${event.id}/register`)}
                className="w-full py-3 rounded-xl bg-[#F4D341] text-[#2B3A67] font-extrabold text-sm hover:bg-[#e6c73a] transition-colors"
                data-testid="tournament-register-btn">
                Register Now
              </button>
            </div>
          )}

          {isRegistrationEvent && (
            <div className="mt-8 bg-white rounded-2xl border border-gray-100 p-6 shadow-sm" data-testid="event-registration-form">
              <h3 className="text-lg font-bold mb-4">Player registration details</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {(registrationConfig?.fields || []).map((field) => (
                  <div key={field.id} className="space-y-2">
                    <label
                      data-testid={`registration-field-${field.id}-label`}
                      className="text-xs uppercase tracking-[0.2em] text-gray-400"
                    >
                      {field.label}
                    </label>
                    {field.type === 'select' ? (
                      <select
                        data-testid={`registration-field-${field.id}-select`}
                        value={registrationForm[field.id] || ''}
                        onChange={(e) => handleRegistrationFieldChange(field.id, e.target.value)}
                        className="w-full border-2 border-gray-200 rounded-xl px-3 py-2 text-sm font-medium focus:outline-none focus:border-[#E54562]"
                      >
                        <option value="">Select {field.label.toLowerCase()}</option>
                        {(field.options || []).map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        data-testid={`registration-field-${field.id}-input`}
                        type={field.type === 'url' ? 'url' : 'text'}
                        value={registrationForm[field.id] || ''}
                        onChange={(e) => handleRegistrationFieldChange(field.id, e.target.value)}
                        placeholder={`Enter ${field.label.toLowerCase()}`}
                        className="w-full border-2 border-gray-200 rounded-xl px-3 py-2 text-sm font-medium focus:outline-none focus:border-[#E54562]"
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-8">
            <h3 className="font-bold text-lg mb-4" data-testid="event-tickets-title">Select Tickets</h3>
            <p className="text-xs text-gray-400 mb-3" data-testid="event-ticket-limit">Limit: {maxTickets} ticket per registration</p>
            <div className="space-y-3">
              {event.ticket_tiers.map((tier) => (
                <TicketSelector
                  key={tier.id}
                  tier={tier}
                  quantity={booking[tier.id] || 0}
                  onUpdate={updateQty}
                  disableIncrease={totalTickets >= maxTickets}
                />
              ))}
            </div>
          </div>

        </div>
      </main>

      <div
        className={`fixed bottom-6 left-0 right-0 px-4 z-[1100] transition-all duration-1000 ease-out ${
          checkoutVisible
            ? 'translate-y-0 opacity-100'
            : 'translate-y-24 opacity-0 pointer-events-none'
        }`}
        aria-hidden={!checkoutVisible}
      >
        <div className="floating-bar flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>
            <p data-testid="event-total-label" className="text-sm text-gray-500">
              Total ({totalTickets} tickets)
            </p>
            <p data-testid="event-total-amount" className="text-2xl font-bold text-[#2B3A67]">
              {formatCurrency(totalAmount)}
            </p>
          </div>
          <button
            data-testid="event-checkout-button"
            onClick={handleCheckout}
            disabled={submitting}
            className="btn-primary px-10"
          >
            {submitting ? 'Processing...' : 'Continue to Checkout'}
          </button>
        </div>
      </div>
    </div>
  );
}